import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { handleApiError, isAuthError } from '../../utils/api/errorHandling';
import { handleNetworkError, isNetworkError } from '../../utils/api/networkErrors';
import { mockCourses } from '../../data/mockCourses';
import type { Database } from '../../types/database';

type Course = Database['public']['Tables']['courses']['Row'];
type CourseInsert = Database['public']['Tables']['courses']['Insert'];
type CourseModule = Database['public']['Tables']['course_modules']['Row'];
type CourseLesson = Database['public']['Tables']['course_lessons']['Row'];

export async function getCourses() {
  try {
    // Check if offline
    if (!navigator.onLine) {
      console.log('Offline mode: Using mock data');
      return mockCourses;
    }

    const { data, error } = await supabase
      .from('courses') 
      .select(`
        *,
        course_marketplace_listings (
          price,
          sale_price,
          sale_ends_at
        )
      `)
      .eq('deleted', false)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Database error:', error);
      return mockCourses;
    }

    return data || mockCourses;
  } catch (error) {
    console.error('Error fetching courses:', error);
    if (isNetworkError(error)) {
      toast.error('Connection error. Using offline data.', {
        id: 'offline-courses'
      });
    }
    return mockCourses;
  }
}

async function getCourse(courseId: string) {
  try {
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        course_marketplace_listings (
          price,
          sale_price,
          sale_ends_at
        )
      `)
      .eq('id', courseId)
      .is('deleted', false)
      .single();

    if (error) {
      console.error('Database error:', error);
      return mockCourses.find(c => c.id === courseId) || null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching course:', error);
    handleNetworkError(error);
    return mockCourses.find(c => c.id === courseId) || null;
  }
}

export async function createCourse(courseData: Partial<Course>) {
  try {
    // Validate required fields first
    const errors = [];
    if (!courseData.title?.trim()) errors.push('Course title is required');
    if (!courseData.description?.trim()) errors.push('Course description is required');
    if (!courseData.image_url?.trim()) errors.push('Course image is required');
    if (!courseData.duration?.trim()) errors.push('Course duration is required');
    if (!courseData.provider?.trim()) errors.push('Course provider is required');

    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }

    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('Authentication required');
    }

    const preparedData = {
      ...courseData,
      title: courseData.title.trim(),
      description: courseData.description.trim(),
      image_url: courseData.image_url.trim(),
      duration: courseData.duration.trim(),
      level: courseData.level || 'Beginner',
      provider: courseData.provider.trim(),
      affiliate_url: courseData.affiliate_url?.trim() || null,
      is_published: courseData.is_published || false,
      deleted: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data: newCourse, error: courseError } = await supabase
      .from('courses')
      .insert(preparedData)
      .select()
      .maybeSingle();

    if (courseError) throw courseError;
    if (!newCourse) throw new Error('Failed to create course');

    // Then create marketplace listing
    const { error: listingError } = await supabase
      .from('course_marketplace_listings')
      .insert({
        course_id: newCourse.id,
        price: 0,
        created_at: new Date().toISOString()
      });

    if (listingError) {
      console.error('Error creating marketplace listing:', listingError);
      // Don't fail the whole operation if listing creation fails
    }
    
    toast.success('Course created successfully');
    return newCourse;
  } catch (error) {
    console.error('Error creating course:', error);
    if (error instanceof Error) {
      toast.error(error.message);
    } else {
      toast.error('Failed to create course');
    }
    throw error;
  }
}

export async function updateCourse(courseId: string, courseData: Partial<Course>) {
  try {
    // Validate required fields first
    const errors = [];
    if (!courseData.title?.trim()) errors.push('Course title is required');
    if (!courseData.description?.trim()) errors.push('Course description is required');
    if (!courseData.image_url?.trim()) errors.push('Course image is required');
    if (!courseData.duration?.trim()) errors.push('Course duration is required');
    if (!courseData.provider?.trim()) errors.push('Course provider is required');

    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }

    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('Authentication required');

    const cleanedData = {
      title: courseData.title?.trim(),
      description: courseData.description?.trim(),
      image_url: courseData.image_url?.trim(),
      duration: courseData.duration?.trim(),
      level: courseData.level || 'Beginner',
      provider: courseData.provider?.trim(),
      affiliate_url: courseData.affiliate_url?.trim() || null,
      is_published: courseData.is_published || false,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('courses')
      .update(cleanedData)
      .eq('id', courseId)
      .select('*')
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new Error('Course not found');
      }
      throw error;
    }
    if (!data) throw new Error('Course not found');

    toast.success('Course updated successfully');
    return data;
  } catch (error) {
    console.error('Error updating course:', error);
    if (error instanceof Error) {
      toast.error(error.message);
    } else {
      toast.error('Failed to update course');
    }
    throw error;
  }
}

async function createModule(moduleData: {
  course_id: string;
  title: string;
  duration: string;
  sequence_number: number;
}) {
  const { data, error } = await supabase
    .from('course_modules')
    .insert(moduleData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function createLesson(lessonData: {
  module_id: string;
  title: string;
  duration: string;
  type: 'video' | 'reading' | 'quiz';
  content: string;
  sequence_number: number;
}) {
  const { data, error } = await supabase
    .from('course_lessons')
    .insert(lessonData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateModule(moduleId: string, moduleData: Partial<CourseModule>) {
  const { data, error } = await supabase
    .from('course_modules')
    .update(moduleData)
    .eq('id', moduleId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateLesson(lessonId: string, lessonData: Partial<CourseLesson>) {
  const { data, error } = await supabase
    .from('course_lessons')
    .update(lessonData)
    .eq('id', lessonId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteModule(moduleId: string) {
  const { error } = await supabase
    .from('course_modules')
    .delete()
    .eq('id', moduleId);

  if (error) throw error;
}

export async function deleteCourse(courseId: string) {
  try {
    // Check auth first to fail fast
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('Authentication required');
    }

    // Soft delete by updating the deleted flag
    const { error } = await supabase
      .from('courses')
      .update({ 
        deleted: true, 
        updated_at: new Date().toISOString() 
      })
      .eq('id', courseId);

    if (error) throw error;
    toast.success('Course deleted successfully');
  } catch (error) {
    console.error('Error deleting course:', error);
    throw error;
  }
}
async function deleteLesson(lessonId: string) {
  const { error } = await supabase
    .from('course_lessons')
    .delete()
    .eq('id', lessonId);

  if (error) throw error;
}

async function deleteAllCourses() {
  try {
    // Check auth first to fail fast
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('Authentication required');
    }

    // Check if user is admin
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single();

    if (profileError || !profile?.is_admin) {
      throw new Error('Admin privileges required');
    }

    // Soft delete all courses
    const { error: deleteError } = await supabase
      .from('courses')
      .update({ 
        deleted: true,
        updated_at: new Date().toISOString()
      })
      .is('deleted', false);

    if (deleteError) throw deleteError;
    
    toast.success('All courses have been deleted');
  } catch (error) {
    console.error('Error deleting all courses:', error);
    toast.error('Failed to delete courses');
    throw error;
  }
}